import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Atom, BookOpen, Users, Award, TrendingUp, FileText, Download } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const Research = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  const researchAreas = [
    {
      icon: <Atom className="w-8 h-8" />,
      title: 'Quantum Algorithms',
      description: 'Developing novel quantum algorithms for optimization, machine learning, and cryptography',
      projects: 12,
      publications: 28,
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Quantum Error Correction',
      description: 'Advancing error mitigation techniques for fault-tolerant quantum computation',
      projects: 8,
      publications: 15,
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Superconducting Qubits',
      description: 'Improving qubit coherence times and gate fidelities in superconducting systems',
      projects: 10,
      publications: 22,
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Quantum Networking',
      description: 'Building quantum communication protocols and distributed quantum computing',
      projects: 6,
      publications: 11,
    },
  ];

  const publications = [
    {
      title: 'Scalable Quantum Error Correction via Graph Neural Networks',
      authors: 'Chen, L., Rodriguez, M., Patel, S.',
      journal: 'Nature Quantum Information',
      year: '2025',
      citations: 142,
    },
    {
      title: 'High-Fidelity Superconducting Qubit Gates Using Optimal Control',
      authors: 'Thompson, R., Kim, J., Anderson, P.',
      journal: 'Physical Review Letters',
      year: '2024',
      citations: 89,
    },
    {
      title: 'Quantum Machine Learning for Drug Discovery Applications',
      authors: 'Martinez, A., Singh, K., Brown, T.',
      journal: 'Science Advances',
      year: '2024',
      citations: 156,
    },
    {
      title: 'Entanglement Distribution in Multi-Node Quantum Networks',
      authors: 'Zhang, Y., Wilson, E., Gupta, R.',
      journal: 'Nature Communications',
      year: '2024',
      citations: 73,
    },
  ];

  const stats = [
    { value: '150+', label: 'Publications', icon: <FileText className="w-6 h-6" /> },
    { value: '50+', label: 'Active Projects', icon: <Atom className="w-6 h-6" /> },
    { value: '25+', label: 'Research Partners', icon: <Users className="w-6 h-6" /> },
    { value: '15+', label: 'Awards', icon: <Award className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[60vh] flex items-center ${classes.bg} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 ${theme === 'light' ? 'bg-blue-400' : theme === 'dark' ? 'bg-cyan-400' : 'bg-purple-400'} rounded-full opacity-30 animate-float`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${5 + Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center animate-slide-in-up">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
              Quantum Research &{' '}
              <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                Innovation
              </span>
            </h1>
            <p className={`text-lg sm:text-xl ${classes.subtext} max-w-3xl mx-auto`}>
              Pushing the boundaries of quantum computing through groundbreaking research,
              collaborative partnerships, and innovative solutions for tomorrow's challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-12 sm:py-16 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} text-center animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                  {stat.icon}
                </div>
                <div className={`text-2xl sm:text-3xl font-bold ${classes.text} mb-2`}>{stat.value}</div>
                <div className={`text-sm ${classes.subtext}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Research Areas
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Exploring the frontiers of quantum science and technology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {researchAreas.map((area, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} hover:border-opacity-50 transition-all animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-6`}>
                  {area.icon}
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${classes.text} mb-3`}>{area.title}</h3>
                <p className={`${classes.subtext} mb-6 text-sm sm:text-base`}>{area.description}</p>
                <div className="flex gap-6">
                  <div>
                    <div className={`text-2xl font-bold ${classes.text}`}>{area.projects}</div>
                    <div className={`text-sm ${classes.subtext}`}>Projects</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${classes.text}`}>{area.publications}</div>
                    <div className={`text-sm ${classes.subtext}`}>Publications</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Recent Publications
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Latest contributions to quantum science
            </p>
          </div>

          <div className="space-y-6">
            {publications.map((pub, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-xl p-6 border ${classes.cardBorder} hover:border-opacity-50 transition-all animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className={`text-lg sm:text-xl font-bold ${classes.text} mb-2`}>
                      {pub.title}
                    </h3>
                    <p className={`${classes.subtext} text-sm mb-2`}>{pub.authors}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <span className={classes.accentText}>{pub.journal}</span>
                      <span className={classes.subtext}>{pub.year}</span>
                      <span className={classes.subtext}>• {pub.citations} citations</span>
                    </div>
                  </div>
                  <button className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${classes.cardBorder} ${classes.accentText} hover:bg-opacity-10 transition-all`}>
                    <Download className="w-4 h-4" />
                    <span className="text-sm">PDF</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/publications"
              className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transition-all`}
            >
              View All Publications
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Collaborate With Us
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Join our research community and contribute to the future of quantum computing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
            >
              Contact Research Team
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/careers"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' : 
                theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' : 
                'border-purple-500 text-purple-400 hover:bg-purple-950'
              } transition-all`}
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Research;